import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import { Modal } from '../../components/ui/Modal.jsx'
import { Field, Input, Select, Textarea } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'
import { formatCurrency, formatDate } from '../../lib/format.js'
import { AdminTable, Td } from './AdminTable.jsx'

/*
 * Admin — billing plans CRUD (super-admin). GET/POST/PUT/DELETE
 * /admin/billing/plans. Each plan carries a base price (monthly/annual) plus the
 * usage pricing surfaced on the landing & registration.
 */
export default function PlansTab() {
  const { t } = useTranslation()
  const { confirm, toast, notify } = useFeedback()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // plan object or {} for new
  const [viewing, setViewing] = useState(null) // plan whose companies we list

  const load = useCallback(() => {
    setLoading(true)
    api.get('/admin/billing/plans').then(({ data }) => setRows((data?.plans || []).slice().sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)))).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const remove = async (p) => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: t('admin.plans.confirmDelete'), message: p.name, confirmLabel: t('common.delete'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete(`/admin/billing/plans/${p.id}`); toast(t('admin.plans.deleted'), 'success'); load() }
    catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('admin.plans.deleteError') }) }
  }

  const columns = [
    { key: 'name', label: t('admin.plans.name') }, { key: 'period', label: t('admin.plans.period') },
    { key: 'monthly', label: t('admin.plans.monthly'), align: 'right' }, { key: 'usage', label: t('admin.plans.usage') },
    { key: 'companies', label: t('admin.plans.companies'), align: 'right' }, { key: 'status', label: t('admin.companies.status') },
    { key: 'actions', label: t('common.actions'), align: 'right' },
  ]

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <Button variant="primary" icon="plus" onClick={() => setEditing({})}>{t('admin.plans.new')}</Button>
      </div>
      <AdminTable columns={columns} rows={rows} loading={loading} empty={t('admin.plans.empty')}
        renderRow={(p) => (
          <>
            <Td strong>{p.name}{p.is_free && <span style={{ marginLeft: 6 }}><Badge tone="info">{t('admin.plans.free')}</Badge></span>}{p.is_default && <span style={{ marginLeft: 6 }}><Badge tone="neutral">{t('admin.plans.default')}</Badge></span>}{p.is_visible === false && <span style={{ marginLeft: 6 }}><Badge tone="warning">{t('admin.plans.hidden')}</Badge></span>}</Td>
            <Td>{p.billing_period === 'yearly' ? t('register.year') : t('register.month')}</Td>
            <Td align="right">{p.is_free ? '—' : formatCurrency(p.monthly_base_price)}</Td>
            <Td><span style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{formatCurrency(p.price_per_outgoing_invoice)}/{formatCurrency(p.price_per_incoming_invoice)} · {p.included_stored_invoices}</span></Td>
            <Td align="right">{p.company_count ?? 0}</Td>
            <Td><Badge tone={p.is_active ? 'success' : 'neutral'}>{p.is_active ? t('admin.active') : t('admin.inactive')}</Badge></Td>
            <Td align="right"><div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <IconButton icon="customers" title={t('admin.plans.viewCompanies')} onClick={() => setViewing(p)} />
              <IconButton icon="pencil" variant="primary" title={t('common.edit')} onClick={() => setEditing(p)} />
              <IconButton icon="trash" variant="danger" title={t('common.delete')} disabled={(p.company_count ?? 0) > 0} onClick={() => remove(p)} />
            </div></Td>
          </>
        )} />
      {editing && <PlanModal t={t} plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load() }} toast={toast} notify={notify} />}
      {viewing && <CompaniesOnPlanModal t={t} plan={viewing} onClose={() => setViewing(null)} />}
    </>
  )
}

const NUM = ['monthly_base_price', 'annual_base_price', 'price_per_outgoing_invoice', 'price_per_incoming_invoice', 'price_per_100_extra_invoices', 'included_stored_invoices', 'display_order']

function PlanModal({ t, plan, onClose, onSaved, toast, notify }) {
  const isNew = !plan.id
  const [f, setF] = useState({
    name: '', description: '', billing_period: 'monthly', monthly_base_price: 0, annual_base_price: 0,
    price_per_outgoing_invoice: 0.25, price_per_incoming_invoice: 0.25, price_per_100_extra_invoices: 1,
    included_stored_invoices: 500, display_order: 0, is_active: true, is_visible: true, is_free: false, ...plan,
  })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))

  const save = async () => {
    if (!f.name.trim()) return
    setBusy(true)
    const payload = { ...f }
    NUM.forEach((k) => { payload[k] = Number(f[k]) || 0 })
    try {
      if (isNew) await api.post('/admin/billing/plans', payload)
      else await api.put(`/admin/billing/plans/${plan.id}`, payload)
      toast(isNew ? t('admin.plans.created') : t('admin.plans.saved'), 'success'); onSaved()
    } catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }) } finally { setBusy(false) }
  }

  const numField = (k, label, step = '0.01') => <Field label={label}><Input type="number" step={step} min="0" value={f[k]} onChange={(e) => set(k, e.target.value)} /></Field>

  return (
    <Modal open onClose={onClose} maxWidth={620} closeOnBackdrop={false}>
      <div style={{ padding: 26, maxHeight: '88vh', overflowY: 'auto' }}>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, marginBottom: 18 }}>{isNew ? t('admin.plans.new') : f.name}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
          <Field label={t('admin.plans.name')} required><Input value={f.name} onChange={(e) => set('name', e.target.value)} /></Field>
          <Field label={t('admin.plans.period')}><Select value={f.billing_period} onChange={(e) => set('billing_period', e.target.value)}><option value="monthly">{t('register.month')}</option><option value="yearly">{t('register.year')}</option></Select></Field>
        </div>
        <Field label={t('admin.plans.description')}><Textarea rows={2} value={f.description || ''} onChange={(e) => set('description', e.target.value)} /></Field>

        <SectionLabel>{t('admin.plans.pricing')}</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {numField('monthly_base_price', t('admin.plans.monthlyBase'))}
          {numField('annual_base_price', t('admin.plans.annualBase'))}
          {numField('price_per_outgoing_invoice', t('admin.plans.perOutgoing'))}
          {numField('price_per_incoming_invoice', t('admin.plans.perIncoming'))}
          {numField('included_stored_invoices', t('admin.plans.includedStorage'), '1')}
          {numField('price_per_100_extra_invoices', t('admin.plans.per100Extra'))}
        </div>

        <SectionLabel>{t('admin.plans.options')}</SectionLabel>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
          {[['is_active', t('admin.plans.optActive')], ['is_visible', t('admin.plans.optVisible')], ['is_free', t('admin.plans.optFree')]].map(([k, l]) => (
            <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={!!f[k]} onChange={(e) => set(k, e.target.checked)} /> {l}
            </label>
          ))}
          <div style={{ width: 120 }}>{numField('display_order', t('admin.plans.order'), '1')}</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" icon="check" loading={busy} disabled={!f.name.trim()} onClick={save}>{isNew ? t('admin.plans.create') : t('common.save')}</Button>
        </div>
      </div>
    </Modal>
  )
}

function SectionLabel({ children }) {
  return <h4 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 13, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em', margin: '14px 0 10px' }}>{children}</h4>
}

function CompaniesOnPlanModal({ t, plan, onClose }) {
  const [companies, setCompanies] = useState(null)
  useEffect(() => {
    api.get(`/admin/billing/plans/${plan.id}/companies`).then(({ data }) => setCompanies(data?.companies || [])).catch(() => setCompanies([]))
  }, [plan.id])
  return (
    <Modal open onClose={onClose} maxWidth={620}>
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '82vh' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--color-border-row)' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 17 }}>{t('admin.plans.companiesOn', { plan: plan.name })}</h3>
          {companies && <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 2 }}>{t('admin.plans.companiesCount', { count: companies.length })}</p>}
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {!companies ? <div style={{ padding: 24, color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
            : companies.length === 0 ? <div style={{ padding: 28, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('admin.plans.noCompanies')}</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: 'var(--color-bg-lilac)' }}>
                  {[t('admin.plans.name'), t('admin.companies.status'), t('onboarding.checklist.nextBilling')].map((h, i) => (
                    <th key={i} style={{ textAlign: 'left', padding: '10px 22px', fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c.company_id} style={{ borderTop: '1px solid var(--color-border-row)' }}>
                      <td style={{ padding: '11px 22px', fontSize: 14, color: 'var(--color-ink)', fontWeight: 500 }}>{c.company_name}</td>
                      <td style={{ padding: '11px 22px' }}><Badge tone={c.status === 'active' ? 'success' : c.status === 'canceled' ? 'danger' : 'neutral'}>{c.status}</Badge></td>
                      <td style={{ padding: '11px 22px', fontSize: 13, color: 'var(--color-text-muted)' }}>{c.next_billing_date ? formatDate(c.next_billing_date) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 22px', borderTop: '1px solid var(--color-border-row)' }}>
          <Button variant="secondary" onClick={onClose}>{t('common.close')}</Button>
        </div>
      </div>
    </Modal>
  )
}
